import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch } from 'react-redux';
import { Logger, Log } from '../utils/Logger';
import { hashString } from '../utils/StringUtils';
import * as enums from '../enums/Enums';
import Cookies from 'universal-cookie';
import { portalApi } from '../redux/services/portal/index.api';
import { resetProjects } from '../redux/reducers/projects.reducer';
import { useCallback } from 'react';
import { getSafeReturnUrl } from '@/utils/redirectUrl';
import { setIsLoggingOut } from '@/utils/logoutState';
import { clearSelectedProjectStorage } from '@/utils/clearSelectedProjectStorage';

const logger = new Logger();

/** Auth0 authorize param; UL signup partial binds via transaction.params.ext-draft_token. */
const AUTH0_DRAFT_TOKEN_PARAM = 'ext-draft_token';

const logUserSession = async (sessionEvent, getIdTokenClaims, auth0User) => {
	let emailHash = null;
	try {
		// Try to get email from ID token claims or user object
		if (getIdTokenClaims) {
			const idTokenClaims = await getIdTokenClaims();
			if (idTokenClaims?.email) {
				emailHash = hashString(idTokenClaims.email);
			}
		} else if (auth0User?.email) {
			emailHash = hashString(auth0User.email);
		}
	} catch (e) {
		// Ignore errors in logging - email hash is optional
	}
	
	const logViewChange = new Log();
	logViewChange.SetLevel(enums.AppInsightLogLevel.EVENT);
	logViewChange.SetName('User_Session');
	logViewChange.AddProperty('Type', sessionEvent);
	logViewChange.AddProperty('UserEmailHash', emailHash);
	logger.doLog(logViewChange);
};

export const useAuth = () => {
	const dispatch = useDispatch();
	const {
		isAuthenticated: auth0IsAuthenticated,
		user: auth0User,
		isLoading: auth0IsLoading,
		loginWithRedirect,
		logout: auth0Logout,
		getAccessTokenSilently,
		getIdTokenClaims
	} = useAuth0();

	// Auth0 SDK manages authentication state - no Redux sync needed
	// Components should use useAuth0() directly for authentication state

	const setLocalReturnUrl = useCallback((returnUrl) => {
		const safeReturnUrl = getSafeReturnUrl(returnUrl);
		if (safeReturnUrl) {
			localStorage.setItem('returnUrl', safeReturnUrl);
		}
	}, []);

	const login = useCallback(async (username, password, callback, returnUrl, options = {}) => {
		setLocalReturnUrl(returnUrl);
		localStorage.setItem('lastAuthAction', 'login');
		await logUserSession('login', getIdTokenClaims, auth0User);
		
		const authorizationParams = {
			...(username ? { login_hint: username } : {}),
		};
		if (options.prompt) {
			authorizationParams.prompt = options.prompt;
		}
		if (options.draftToken) {
			authorizationParams[AUTH0_DRAFT_TOKEN_PARAM] = options.draftToken;
		}

		await loginWithRedirect({
			authorizationParams
		});
	}, [loginWithRedirect, setLocalReturnUrl, getIdTokenClaims, auth0User]);

	const signup = useCallback(async (email, password, source, callback, options = {}) => {
		localStorage.setItem('signupAction', 'create');
		await logUserSession('signup', getIdTokenClaims, auth0User);
		
		await loginWithRedirect({
			authorizationParams: {
				screen_hint: 'signup',
				...(email ? { login_hint: email } : {}),
				...(options.draftToken ? { [AUTH0_DRAFT_TOKEN_PARAM]: options.draftToken } : {}),
			}
		});
	}, [loginWithRedirect, getIdTokenClaims, auth0User]);

	const reset = useCallback(async (email, callback) => {
		await logUserSession('reset password', getIdTokenClaims, auth0User);
		
		await loginWithRedirect({
			authorizationParams: {
				screen_hint: 'forgot-password',
				...(email ? { login_hint: email } : {}),
				connection: 'Username-Password-Authentication'
			}
		});
	}, [loginWithRedirect, getIdTokenClaims, auth0User]);

	// SSO login methods removed - Auth0 Universal Login handles all SSO providers
	// Configure SSO connections in Auth0 Dashboard:
	// - Authentication → Enterprise → [Connection Name]
	// - Enable connection for your SPA application
	// - Auth0 Universal Login will automatically show SSO options
	// - Email domain auto-routing can be configured via Identifier First Authentication


	const logout = useCallback(async (returnTo = '/') => {
		setIsLoggingOut(true);
		await logUserSession('logout', getIdTokenClaims, auth0User);

		// Clear local storage (except activeWindows)
		const activeWindow = localStorage.getItem('activeWindows');
		localStorage.clear();
		localStorage.setItem('activeWindows', activeWindow);
		// sessionStorage project selection survives localStorage.clear()
		clearSelectedProjectStorage();
		
		// Clear RTK Query cache
		dispatch(portalApi.util.invalidateTags(['user']));
		dispatch(portalApi.util.resetApiState());
		
		// Clear current project from Redux
		dispatch(resetProjects());
		
		// Clear cookies
		const domain = window.location.hostname === 'localhost' ? 'localhost' : 'metabolon.com';
		const db = new Cookies();
		
		try {
			db.remove('binf_auth_cookie', {
				path: '/',
				domain: domain,
				secure: window.location.protocol === 'https:',
				sameSite: 'strict'
			});
		} catch (e) {
			// Cookie might not exist, ignore error
		}
		
		// Logout from Auth0
		await auth0Logout({ 
			logoutParams: {
				returnTo: `${window.location.origin}${returnTo}`,
				federated: true
			}
		});
	}, [auth0Logout, dispatch, getIdTokenClaims, auth0User]);

	const clearStorage = useCallback(() => {
		const activeWindow = localStorage.getItem('activeWindows');
		
		localStorage.clear();
		localStorage.setItem('activeWindows', activeWindow);
		clearSelectedProjectStorage();
		
		// Clear RTK Query cache
		dispatch(portalApi.util.invalidateTags(['user']));
		dispatch(portalApi.util.resetApiState());
		
		// Clear current project from Redux
		dispatch(resetProjects());
	}, [dispatch]);

	const isAuthenticated = useCallback(() => {
		return auth0IsAuthenticated;
	}, [auth0IsAuthenticated]);

	return {
		login,
		signup,
		reset,
		// SSO methods removed - handled by Auth0 Universal Login
		handleAuthentication: () => {}, // No longer needed
		logout,
		clearStorage,
		isAuthenticated,
		getAccessTokenSilently,
		getIdTokenClaims
	};
};
